/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useMemo } from 'react';

import { useAppContext } from '../../../../common/ui/app-context';
import { IntentMode } from '../../../../common/utils/intent-detection';
import { getWorkItemPageUrl } from '../../../../common/utils/quick-find/jira-work-item-navigation';
import { useSelectedFilters } from '../../../../controllers/filters/selected';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';
import { ISSUE_NAVIGATOR_URL } from '../search-page-link/utils';

import { isSamePathname } from './url-matching';

export type IntentRoutesResult = {
	defaultMode: IntentMode;
	allAppsUrl: string;
	jiraWorkItemsUrl: string;
	isAllAppsPage: boolean;
	isJiraWorkItemsPage: boolean;
};

/**
 * Determine the default mode and URLs for the intent routing.
 */
export const useIntentRoutes = (): IntentRoutesResult => {
	const { generateSearchUrl, queryParams } = useAppContext();
	const quickFindQuery = useQuickFindQuery();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});

	const allAppsUrl = useMemo(
		() =>
			generateSearchUrl({
				...queryParams,
				text: quickFindQuery,
			}),
		[generateSearchUrl, queryParams, quickFindQuery],
	);

	const jiraWorkItemsUrl = useMemo(() => {
		try {
			return getWorkItemPageUrl({
				query: quickFindQuery,
				jiraFilters,
				generateSearchUrl,
				queryParams,
			});
		} catch {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [generateSearchUrl, jiraFilters, queryParams, quickFindQuery]);

	const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

	const isAllAppsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, allAppsUrl) : false),
		[currentUrl, allAppsUrl],
	);
	const isJiraWorkItemsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, jiraWorkItemsUrl) : false),
		[currentUrl, jiraWorkItemsUrl],
	);

	return {
		defaultMode: isAllAppsPage ? IntentMode.FPS : IntentMode.GNIN,
		allAppsUrl,
		jiraWorkItemsUrl,
		isAllAppsPage,
		isJiraWorkItemsPage,
	};
};
